import React, { useState, useEffect, useCallback } from 'react';
import { Card, Container, Button, Spinner, ButtonGroup } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import LimitModal from './LimitModal';
import Ad from './Ad';

function NsfwInfinityScroll() {
  const [items, setItems] = useState([]);
  const [iterator, setIterator] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  const retrieveData = useCallback(async () => {
    if (loading || !hasMore) return;

    // Check for scroll limit
    if (window.limitedScroll) {
      const scrollCount = parseInt(localStorage.getItem('scrollCount') || '0');
      if (scrollCount >= window.scrollLimit && !localStorage.no_limit) {
        setShowLimitModal(true);
        return;
      }
      // Increment scroll count
      localStorage.setItem('scrollCount', (scrollCount + 1).toString());
    }

    setLoading(true);
    
    try {
      const urlParams = new URLSearchParams(location.search);
      const subParam = urlParams.get('sub');
      const queryParams = new URLSearchParams();

      if (!initialLoad && iterator) {
        queryParams.append('iterator', iterator);
      }
      if (subParam) {
        queryParams.append('sub', subParam);
      }

      const apiUrl = `https://api.qwerty6-4e9.workers.dev/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await fetch(apiUrl);
      const data = await response.json();

      if (subParam) {
        const newItems = data.data.getSubreddit.children.items.map(item => ({
          title: item.title,
          media: item.mediaSources.reduce((largest, current) => {
            return (current.width * current.height > largest.width * largest.height) ? current : largest;
          }, item.mediaSources[0]),
          videoSources: item.mediaSources.filter(source => 
            source.url.endsWith('.mp4') || source.url.endsWith('.webm')
          ),
          isVideo: item.mediaSources.some(source => 
            source.url.endsWith('.mp4') || source.url.endsWith('.webm')
          ),
          hasAudio: item.hasAudio,
          parentTitle: item.subredditTitle,
          parentUrl: item.subredditUrl
        }));
        
        if (newItems.length === 0) {
          setHasMore(false);
        } else {
          setItems(prevItems => initialLoad ? newItems : [...prevItems, ...newItems]);
          setIterator(data.data.getSubreddit.children.iterator);
        }
      } else {
        const newGroups = data.data.discoverSubreddits.items.map(subreddit => ({
          id: subreddit.id,
          title: subreddit.title,
          url: subreddit.url,
          banner: subreddit.banner?.[0],
          items: subreddit.children.items.map(item => ({
            title: item.title,
            media: item.mediaSources.reduce((smallest, current) => {
              return (current.width * current.height < smallest.width * smallest.height) ? current : smallest;
            }, item.mediaSources[0]),
            videoSources: item.mediaSources
              .filter(source => source.url.endsWith('.mp4') || source.url.endsWith('.webm'))
              .sort((a, b) => (a.width * a.height) - (b.width * b.height))
              .slice(0, 1),
            isVideo: item.mediaSources.some(source => 
              source.url.endsWith('.mp4') || source.url.endsWith('.webm')
            ),
            hasAudio: item.hasAudio
          }))
        }));
        
        if (newGroups.length === 0) {
          setHasMore(false);
        } else {
          setItems(prevItems => initialLoad ? newGroups : [...prevItems, ...newGroups]);
          setIterator(data.data.discoverSubreddits.iterator);
        }
      }
      setInitialLoad(false);
    } catch (error) {
      console.error('Error fetching items:', error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [location.search, iterator, loading, initialLoad, hasMore]);

  const handleScroll = useCallback(() => {
    if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 100) {
      retrieveData();
    }
  }, [retrieveData]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    // Trigger initial load when component mounts
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleSubredditClick = (url) => {
    const subreddit = url.replace(/^\/r\//, '');
    const newUrl = `${window.location.origin}/nsfw_infinity_scroll?sub=${encodeURIComponent(subreddit)}`;
    window.open(newUrl, '_blank');
  };

  const urlParams = new URLSearchParams(location.search);
  const subParam = urlParams.get('sub');

  return (
    <>
    <LimitModal show={showLimitModal} onHide={() => {}} />
    <Container className="nsfw-infinity-scroll">
      <div className="d-flex flex-column align-items-center mb-4">
        <Ad width={728} height={90} />
        <Ad width={468} height={60} />
        <Ad width={320} height={50} />
      </div>
      
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
        <Button variant="outline-primary" onClick={() => navigate(-1)}>
          ← Back
        </Button>
          
        </div>
        <ButtonGroup>
          <Button variant="outline-primary" className="" onClick={() => navigate('/')}>
            Home
          </Button>
          <Button variant="outline-primary" onClick={() => navigate('/category_selection')}>
            Back to Search
          </Button>
        </ButtonGroup>
      </div>

      {items.length === 0 && !loading && !initialLoad ? (
        <div className="alert alert-warning text-center my-4">
          This category has no posts available
        </div>
      ) : items.length === 0 && !loading ? (
        <div className="text-center py-5">
          <p>Scroll down to load content</p>
        </div>
      ) : null}

      {!subParam ? (
        // Featured view with horizontal scrolling groups
        items.map((group, groupIndex) => (
          <div key={groupIndex} className="mb-5">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3 className="m-0">{group.title}</h3>
              <Button 
                variant="outline-light" 
                onClick={() => handleSubredditClick(group.url)}
              >
                View More
              </Button>
            </div>
            <div 
              className="d-flex overflow-auto pb-3" 
              style={{ gap: '1rem', scrollSnapType: 'x mandatory' }}
            >
              {group.items.map((item, index) => (
                <Card 
                  key={index} 
                  className="bg-dark text-light flex-shrink-0"
                  style={{ 
                    width: '300px',
                    scrollSnapAlign: 'start'
                  }}
                >
                  {item.isVideo ? (
                    <video
                      style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                      controls={item.hasAudio}
                      muted={!item.hasAudio}
                      autoPlay={!item.hasAudio}
                      loop
                    >
                      {item.videoSources.map((source, idx) => (
                        <source key={idx} src={source.url} type={`video/${source.url.split('.').pop()}`} />
                      ))}
                    </video>
                  ) : (
                    <Card.Img 
                      variant="top" 
                      src={item.media.url} 
                      alt={item.title}
                      style={{ height: '200px', objectFit: 'cover' }}
                    />
                  )}
                  <Card.Body>
                    <Card.Title style={{ fontSize: '0.9rem' }}>{item.title}</Card.Title>
                  </Card.Body>
                </Card>
              ))}
            </div>
          </div>
        ))
      ) : (
        // Filtered view (single subreddit)
        items.map((item, index) => (
          <Card key={index} className="bg-dark text-light mb-4" style={{ maxWidth: '100%', width: `${Math.min(item.media.width, 800)}px`, margin: '0 auto' }}>
            {item.isVideo ? (
              <video
                style={{ width: '100%', height: 'auto', maxHeight: '80vh', objectFit: 'contain' }}
                controls={item.hasAudio}
                muted={!item.hasAudio}
                autoPlay={!item.hasAudio}
                loop
              >
                {item.videoSources.map((source, idx) => (
                  <source key={idx} src={source.url} type={`video/${source.url.split('.').pop()}`} />
                ))}
              </video>
            ) : (
              <Card.Img 
                variant="top" 
                src={item.media.url} 
                alt={item.title} 
                style={{ width: '100%', height: 'auto', maxHeight: '80vh', objectFit: 'contain' }} 
              />
            )}
            <Card.Body>
              <Card.Title>{item.title}</Card.Title>
            </Card.Body>
          </Card>
        ))
      )}

      {!hasMore && items.length > 0 && (
        <div className="alert alert-info text-center my-4">
          No more posts available
        </div>
      )}

      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" variant="light" />
        </div>
      )}
    </Container>
  </>
  );
}

export default NsfwInfinityScroll;
